import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SESSION_CHECK_INTERVAL = 60000; // Check every 1 minute
const WARNING_THRESHOLD_MINUTES = 2;

/**
 * Session Monitor Hook
 * Monitors session timeout and provides warnings before expiration
 * Automatically logs out user on session timeout
 */
export const useSessionMonitor = () => {
  const [sessionWarning, setSessionWarning] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [sessionInfo, setSessionInfo] = useState({
    timeoutMinutes: 30,
    minutesRemaining: 30,
    showWarning: false
  });
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  /**
   * Check session status via API response headers
   */
  const checkSession = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        return;
      }

      // Make a lightweight API call to check session
      const response = await api.get('/auth/me', {
        validateStatus: (status) => status < 500 // Don't throw on 4xx errors
      });

      // Extract session info from headers
      const headerTimeout = response.headers['x-session-timeout-minutes'];
      const headerRemaining = response.headers['x-session-minutes-remaining'];
      const headerWarning = response.headers['x-session-warning'];

      const timeoutMinutes = parseInt(headerTimeout || '30');
      const minutesRemaining = parseInt(headerRemaining || '30');
      const showWarning = headerWarning === 'true';

      setSessionInfo({
        timeoutMinutes,
        minutesRemaining,
        showWarning
      });

      setMinutesRemaining(minutesRemaining);
      setSessionWarning(showWarning);


      // Update last activity time
      lastActivityRef.current = Date.now();

    } catch (error) {
      // Handle session expiration errors
      if (error.response?.data?.code === 'SESSION_TIMEOUT' ||
          error.response?.data?.code === 'SESSION_EXPIRED' ||
          error.response?.data?.code === 'SESSION_INVALID') {

        // Clear auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Stop monitoring
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Redirect to login with message
        navigate('/login', {
          state: {
            message: error.response.data.message || 'Sua sessão expirou. Por favor, faça login novamente.',
            type: 'warning'
          }
        });
      } else if (error.response?.data?.code === 'ACCOUNT_LOCKED') {
        // Handle account lockout
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        navigate('/login', {
          state: {
            message: error.response.data.message,
            type: 'error'
          }
        });
      }
    }
  }, [navigate]);

  /**
   * Extend session by making any API call
   */
  const extendSession = useCallback(async () => {
    try {
      await checkSession();
      setSessionWarning(false);
    } catch (error) {
      console.error('Error extending session:', error);
    }
  }, [checkSession]);

  /**
   * Manually lock/end session
   */
  const lockSession = useCallback(async () => {
    try {
      await api.post('/auth/lock-session');
    } catch (error) {
      console.error('Error locking session:', error);
    } finally {
      // Clear auth data regardless of API success
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Stop monitoring
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      navigate('/login', {
        state: {
          message: 'Sessão bloqueada. Por favor, faça login novamente.',
          type: 'info'
        }
      });
    }
  }, [navigate]);

  /**
   * Track user activity to provide better UX
   */
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  /**
   * Start session monitoring
   */
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    // Check immediately on mount
    checkSession();

    // Set up periodic checking
    intervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    // Track user activity (mouse, keyboard, touch)
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(event => {
      window.addEventListener(event, trackActivity);
    });

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, [checkSession, trackActivity]);

  /**
   * Setup axios interceptor to check session on every response
   */
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => {
        // Extract session info from headers on every response
        const headerTimeout = response.headers['x-session-timeout-minutes'];
        const headerRemaining = response.headers['x-session-minutes-remaining'];
        const headerWarning = response.headers['x-session-warning'];

        const timeoutMinutes = parseInt(headerTimeout || '30');
        const minutesRemaining = parseInt(headerRemaining || '30');
        const showWarning = headerWarning === 'true';

        if (showWarning) {
          setSessionInfo({
            timeoutMinutes,
            minutesRemaining,
            showWarning
          });
          setMinutesRemaining(minutesRemaining);
          setSessionWarning(true);
        }

        return response;
      },
      (error) => {
        // Handle session errors on any API call
        if (error.response?.data?.code === 'SESSION_TIMEOUT' ||
            error.response?.data?.code === 'SESSION_EXPIRED' ||
            error.response?.data?.code === 'SESSION_INVALID') {

          localStorage.removeItem('token');
          localStorage.removeItem('user');

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          navigate('/login', {
            state: {
              message: error.response.data.message || 'Sua sessão expirou. Por favor, faça login novamente.',
              type: 'warning'
            }
          });
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  return {
    sessionWarning,
    minutesRemaining,
    sessionInfo,
    extendSession,
    lockSession,
    lastActivity: lastActivityRef.current
  };
};

export default useSessionMonitor;
